import React, { useMemo, useState } from 'react';
import { X, Upload, Loader2, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { importEmployees } from '../api';

interface Props {
  onClose: () => void;
  onDone: () => void;
}

/** 列顺序：姓名 部门 职位 类型 职级 入职日期 月成本 工作量 */
const COLUMNS = ['name', 'department', 'title', 'employee_type', 'level', 'hire_date', 'monthly_cost', 'workload'] as const;
const HEADER_HINT = '姓名\t部门\t职位\t类型\t职级\t入职日期\t月成本\t工作量';

type Row = Record<string, string>;

/** 解析粘贴文本：按行分割，每行优先按 Tab 分隔（从 Excel 复制），否则按逗号 */
function parseRows(text: string): Row[] {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const rows: Row[] = [];
  for (const line of lines) {
    const cells = (line.includes('\t') ? line.split('\t') : line.split(',')).map(c => c.trim());
    // 跳过表头行
    if (cells[0] === '姓名' || cells[0].toLowerCase() === 'name') continue;
    if (!cells[0]) continue;
    const row: Row = {};
    COLUMNS.forEach((key, i) => { row[key] = cells[i] || ''; });
    rows.push(row);
  }
  return rows;
}

export default function EmployeeBatchImportModal({ onClose, onDone }: Props) {
  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);
  const rows = useMemo(() => parseRows(text), [text]);

  const handleImport = async () => {
    if (rows.length === 0) { toast.error('没有可导入的数据，请粘贴表格内容'); return; }
    setImporting(true);
    try {
      const res = await importEmployees(rows);
      const failed = res.failed?.length || 0;
      if (failed > 0) {
        toast.success(`导入完成：成功 ${res.created} 条，失败 ${failed} 条`);
        console.warn('导入失败明细：', res.failed);
      } else {
        toast.success(`导入成功 ${res.created} 条`);
      }
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '批量导入失败');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-primary-500" />批量导入员工
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        <div className="text-xs text-gray-500 mb-2 leading-relaxed">
          从 Excel/表格直接复制粘贴到下方（每行一名员工，列以 <b>Tab 或逗号</b> 分隔）。
          列顺序：<span className="text-gray-700 font-medium">姓名 · 部门 · 职位 · 类型 · 职级 · 入职日期 · 月成本 · 工作量</span>。
          仅<b>姓名必填</b>；部门按名称匹配组织架构，匹配不到则留空（未分配）。
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3 text-[11px] text-gray-400 font-mono overflow-x-auto whitespace-pre">
          {HEADER_HINT + '\n张三\t珠宝工厂\t生产工艺工程师\t全职\tP5\t2026-01-15\t25000\t高'}
        </div>

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={9}
          placeholder="在此粘贴表格内容…"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none font-mono"
        />

        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-500">
            已解析 <b className="text-primary-600">{rows.length}</b> 条
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
            <button onClick={handleImport} disabled={importing || rows.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-40 flex items-center gap-1.5">
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              导入{rows.length > 0 ? ` ${rows.length} 条` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
